import styles from './SiteHeader.module.scss';

import React, { Suspense, lazy, FC, useRef, useEffect } from 'react';
import { useV2Route } from 'hooks/useV2Route';
import { useSingleContentfulEntry } from 'hooks/useSingleContentfulEntry';
import { ContentType, PromoBanner } from 'api/contentful/contentTypes.cloverdotcom';
import { useLocation } from 'react-router-dom';
import { useScrollPositionContext } from 'components/ScrollPositionProvider/ScrollPositionContext';

/** Only load for V2 routes */
const NavBar = lazy(async () => {
  const module = await import('components/NavBar/NavBar');
  return { default: module.NavBar };
});
const PageHeaderBanner = lazy(async () => {
  const module = await import('components/PageHeaderBanner/PageHeaderBanner');
  return { default: module.PageHeaderBanner };
});

interface ISiteHeaderProps extends React.AllHTMLAttributes<HTMLElement> {
  mainContentRef?: React.RefObject<HTMLDivElement>;
  children?: never;
}

export const SiteHeader: FC<ISiteHeaderProps> = props => {
  const { pathname } = useLocation();
  const isV2 = useV2Route();
  const { isAtPageTop } = useScrollPositionContext();
  const hidePromoBar = isAtPageTop === false;
  const bannerRef = useRef<HTMLDivElement>(null);

  const { data: promoBannerEntry } = useSingleContentfulEntry<PromoBanner>(ContentType.PROMO_BANNER);
  const { hidePromoFromPages } = promoBannerEntry || {};
  const isPromoBannerVisible = !hidePromoFromPages?.includes(pathname);
  const shouldRenderBanner = isV2 && isPromoBannerVisible;
  const bannerHeight = bannerRef.current?.getBoundingClientRect().height || 0;

  useEffect(() => {
    document.body.style.setProperty('--header-banner-height', `${hidePromoBar ? 0 : bannerHeight}px`);
    document.body.style.setProperty('--header-banner-top', `${hidePromoBar ? -bannerHeight : 0}px`);
  }, [bannerHeight, hidePromoBar]);

  return (
    <div className={styles.headerContainer}>
      {shouldRenderBanner && (
        <Suspense fallback={null}>
          <PageHeaderBanner entry={promoBannerEntry} ref={bannerRef} />
        </Suspense>
      )}
      <Suspense fallback={null}>
        <NavBar {...props} />
      </Suspense>
    </div>
  );
};

SiteHeader.displayName = 'SiteHeader';
